/**
 * SLA Catalogue Overview Page
 * 
 * World-class UI/UX for displaying SLA Catalogue with:
 * - Global Configuration
 * - Channel SLAs
 * - Stage SLAs  
 * - Escalation Rules
 * - Exception Codes
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React, { useState, useMemo } from 'react';
import { usePrint } from '../../hooks/usePrint';
import PrintButton from '../../components/PrintButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Clock, 
  AlertTriangle, 
  Settings, 
  Zap, 
  FileText,
  Globe,
  Phone,
  Shield,
  Bell,
  Search,
  ChevronRight,
  Timer,
  Users,
  Target,
  TrendingUp,
  CheckCircle,
  XCircle,
  Pause,
  RotateCcw,
  AlertCircle,
  Calendar,
  MapPin,
  MessageSquare,
  Building2,
  UserCheck,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { BrandSelector } from '../../components/pipeline/BrandSelector';
import { 
  getSLACatalogueRegistry,
  getGlobalConfig,
  getChannelSLAsForBrand,
  getStageSLAsForBrand,
  getEscalationRules,
  getExceptionCodes,
  getSLACounts
} from '../../lib/slaCatalogueRegistry';
import type { 
  PipelineBrand, 
  ChannelSLA, 
  StageSLA, 
  EscalationRule, 
  SLAExceptionCode,
  SLAPriority,
  EscalationTriggerType,
  SLABehavior
} from '../../types';

// =============================================================================
// Main Component
// =============================================================================

const SLACatalogueOverview: React.FC = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { printRef, handlePrint } = usePrint('Almajdouie Motors – SLA Catalogue');
  const [selectedBrand, setSelectedBrand] = useState<PipelineBrand>('Changan');
  const [activeTab, setActiveTab] = useState<string>('channels');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<SLAPriority | 'all'>('all');

  // Get data from registry
  const registry = getSLACatalogueRegistry();
  const globalConfig = getGlobalConfig();
  const counts = getSLACounts();
  
  // Filter by brand
  const channelSLAs = getChannelSLAsForBrand(selectedBrand);
  const stageSLAs = getStageSLAsForBrand(selectedBrand);
  const escalationRules = getEscalationRules();
  const exceptionCodes = getExceptionCodes();

  // Apply search filter
  const filteredChannelSLAs = useMemo(() => {
    return channelSLAs.filter(sla => {
      const matchesSearch = !searchTerm || 
        sla.channel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sla.owner.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = priorityFilter === 'all' || sla.priority === priorityFilter;
      return matchesSearch && matchesPriority;
    });
  }, [channelSLAs, searchTerm, priorityFilter]);

  const filteredStageSLAs = useMemo(() => {
    return stageSLAs.filter(sla => {
      const matchesSearch = !searchTerm || 
        sla.stageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sla.owner.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [stageSLAs, searchTerm]);

  // Tab configuration
  const tabs = [
    { id: 'channels', label: isArabic ? 'قنوات العملاء' : 'Channel SLAs', icon: Phone, count: channelSLAs.length },
    { id: 'stages', label: isArabic ? 'مراحل المسار' : 'Stage SLAs', icon: Zap, count: stageSLAs.length },
    { id: 'escalations', label: isArabic ? 'قواعد التصعيد' : 'Escalations', icon: Bell, count: escalationRules.length },
    { id: 'exceptions', label: isArabic ? 'رموز الاستثناء' : 'Exceptions', icon: Shield, count: exceptionCodes.length },
    { id: 'global', label: isArabic ? 'الإعدادات العامة' : 'Global Config', icon: Settings, count: null },
  ];

  return (
    <div ref={printRef} className="space-y-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Clock className="w-6 h-6" />
                </div>
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {isArabic ? 'كتالوج الاتفاقيات' : 'Service Level Agreements'}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-3">
                {isArabic ? 'كتالوج اتفاقيات مستوى الخدمة' : 'SLA Catalogue'}
              </h1>
              <p className="text-white/80 max-w-2xl text-lg">
                {isArabic 
                  ? registry.descriptionAr || 'تعريفات اتفاقيات مستوى الخدمة وقواعد التصعيد ومعالجة الاستثناءات لجميع قنوات العملاء ومراحل المسار'
                  : registry.description || 'Service Level Agreements, escalation rules, and exception handling for all lead channels and pipeline stages'
                }
              </p>
            </div>
            
            {/* Brand Selector + Print */}
            <div className="flex-shrink-0 flex flex-col gap-3 items-end">
              <PrintButton
                onClick={handlePrint}
                isAr={isArabic}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              />
              <BrandSelector 
                selectedBrand={selectedBrand} 
                onBrandChange={setSelectedBrand}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard 
          label={isArabic ? 'إجمالي الاتفاقيات' : 'Total SLAs'}
          value={counts.totalSLAs}
          icon={<FileText className="w-5 h-5" />}
          color="blue"
        />
        <StatCard 
          label={isArabic ? 'اتفاقيات القنوات' : 'Channel SLAs'}
          value={counts.channelSLAs}
          icon={<Phone className="w-5 h-5" />}
          color="green"
        />
        <StatCard 
          label={isArabic ? 'اتفاقيات المراحل' : 'Stage SLAs'}
          value={counts.stageSLAs}
          icon={<Zap className="w-5 h-5" />}
          color="purple"
        />
        <StatCard 
          label={isArabic ? 'قواعد التصعيد' : 'Escalation Rules'}
          value={counts.escalationRules}
          icon={<Bell className="w-5 h-5" />}
          color="amber"
        />
        <StatCard 
          label={isArabic ? 'رموز الاستثناء' : 'Exception Codes'}
          value={counts.exceptionCodes}
          icon={<Shield className="w-5 h-5" />}
          color="red"
        />
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={isArabic ? 'البحث في الاتفاقيات...' : 'Search SLAs...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
        
        {activeTab === 'channels' && (
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as SLAPriority | 'all')}
            className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 min-w-[160px]"
          >
            <option value="all">{isArabic ? 'جميع الأولويات' : 'All Priorities'}</option>
            <option value="critical">{isArabic ? 'حرج' : 'Critical'}</option>
            <option value="high">{isArabic ? 'عالي' : 'High'}</option>
            <option value="medium">{isArabic ? 'متوسط' : 'Medium'}</option>
            <option value="low">{isArabic ? 'منخفض' : 'Low'}</option>
          </select>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-1 p-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap rounded-lg transition-all
                    ${activeTab === tab.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`
                      px-2.5 py-0.5 text-xs rounded-full font-semibold
                      ${activeTab === tab.id
                        ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }
                    `}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'channels' && (
            <ChannelSLAsTab slas={filteredChannelSLAs} isArabic={isArabic} />
          )}
          {activeTab === 'stages' && (
            <StageSLAsTab slas={filteredStageSLAs} isArabic={isArabic} />
          )}
          {activeTab === 'escalations' && (
            <EscalationsTab rules={escalationRules} isArabic={isArabic} />
          )}
          {activeTab === 'exceptions' && (
            <ExceptionsTab codes={exceptionCodes} isArabic={isArabic} />
          )}
          {activeTab === 'global' && (
            <GlobalConfigTab config={globalConfig} isArabic={isArabic} />
          )}
        </div>
      </div>

      {/* Version Footer */}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {isArabic ? 'الإصدار' : 'Version'} {registry.version} • 
          {isArabic ? ' آخر تحديث: ' : ' Last Updated: '}{registry.lastUpdated}
        </p>
      </div>
    </div>
  );
};

// =============================================================================
// Stat Card Component
// =============================================================================

const StatCard: React.FC<{ 
  label: string; 
  value: number; 
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'amber' | 'red';
}> = ({ label, value, icon, color }) => {
  const colors = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
  };

  const iconColors = {
    blue: 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300',
    green: 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300',
    purple: 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300',
    amber: 'bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-300',
    red: 'bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300',
  };

  return (
    <div className={`rounded-xl border p-5 ${colors[color]} transition-all hover:shadow-md`}>
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${iconColors[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Priority Badge Component
// =============================================================================

const PriorityBadge: React.FC<{ priority: SLAPriority; isArabic: boolean; size?: 'sm' | 'md' }> = ({ 
  priority, 
  isArabic,
  size = 'sm'
}) => {
  const config = {
    critical: { 
      bg: 'bg-red-100 dark:bg-red-900/40', 
      text: 'text-red-700 dark:text-red-300',
      border: 'border-red-200 dark:border-red-800',
      label: isArabic ? 'حرج' : 'Critical',
      icon: <AlertCircle className="w-3.5 h-3.5" />
    },
    high: { 
      bg: 'bg-orange-100 dark:bg-orange-900/40', 
      text: 'text-orange-700 dark:text-orange-300',
      border: 'border-orange-200 dark:border-orange-800',
      label: isArabic ? 'عالي' : 'High',
      icon: <TrendingUp className="w-3.5 h-3.5" />
    },
    medium: { 
      bg: 'bg-yellow-100 dark:bg-yellow-900/40', 
      text: 'text-yellow-700 dark:text-yellow-300',
      border: 'border-yellow-200 dark:border-yellow-800',
      label: isArabic ? 'متوسط' : 'Medium',
      icon: <Target className="w-3.5 h-3.5" />
    },
    low: { 
      bg: 'bg-green-100 dark:bg-green-900/40', 
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-200 dark:border-green-800',
      label: isArabic ? 'منخفض' : 'Low',
      icon: <CheckCircle className="w-3.5 h-3.5" />
    },
  };
  
  const c = config[priority];
  const sizeClasses = size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${sizeClasses} ${c.bg} ${c.text} ${c.border}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

// =============================================================================
// Channel SLAs Tab - World Class Cards
// =============================================================================

const ChannelSLAsTab: React.FC<{ slas: ChannelSLA[]; isArabic: boolean }> = ({ 
  slas, 
  isArabic 
}) => {
  if (slas.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <Phone className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {isArabic ? 'لا توجد اتفاقيات قنوات' : 'No Channel SLAs'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {isArabic ? 'لا توجد اتفاقيات قنوات متاحة للعلامة التجارية المحددة' : 'No channel SLAs available for the selected brand'}
        </p>
      </div>
    );
  }

  const getChannelIcon = (channelType: string) => {
    const icons: Record<string, React.ReactNode> = {
      website: <Globe className="w-6 h-6" />,
      oem_portal: <Building2 className="w-6 h-6" />,
      whatsapp: <MessageSquare className="w-6 h-6" />,
      call_center: <Phone className="w-6 h-6" />,
      walk_in: <MapPin className="w-6 h-6" />,
      social_media: <Users className="w-6 h-6" />,
      referral: <UserCheck className="w-6 h-6" />,
      marketplace: <Building2 className="w-6 h-6" />,
    };
    return icons[channelType] || <Globe className="w-6 h-6" />;
  };

  const getChannelGradient = (channelType: string) => {
    const gradients: Record<string, string> = {
      website: 'from-blue-500 to-cyan-500',
      oem_portal: 'from-purple-500 to-indigo-500',
      whatsapp: 'from-green-500 to-emerald-500',
      call_center: 'from-orange-500 to-amber-500',
      walk_in: 'from-rose-500 to-pink-500',
      social_media: 'from-violet-500 to-purple-500',
      referral: 'from-teal-500 to-cyan-500',
      marketplace: 'from-slate-500 to-gray-500',
    };
    return gradients[channelType] || 'from-gray-500 to-slate-500';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Phone className="w-5 h-5 text-green-500" />
          {isArabic ? 'اتفاقيات مستوى الخدمة حسب القناة' : 'Channel SLAs'}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {slas.length} {isArabic ? 'قناة' : 'channels'}
        </span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {slas.map((sla) => (
          <div 
            key={sla.id}
            className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            {/* Card Header with Gradient */}
            <div className={`bg-gradient-to-br ${getChannelGradient(sla.channelType)} p-6 text-white`}>
              <div className="flex items-start justify-between">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  {getChannelIcon(sla.channelType)}
                </div>
                <PriorityBadge priority={sla.priority} isArabic={isArabic} />
              </div>
              <h4 className="text-xl font-bold mt-4">
                {isArabic ? sla.channelAr : sla.channel}
              </h4>
              <p className="text-white/80 text-sm mt-1">
                {isArabic ? sla.ownerAr : sla.owner}
              </p>
            </div>
            
            {/* Card Body */}
            <div className="p-6 space-y-4">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <Timer className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">
                      {isArabic ? 'أول إجراء' : 'First Action'}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {isArabic ? sla.timeToFirstActionAr : sla.timeToFirstAction}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <Phone className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">
                      {isArabic ? 'أول اتصال' : 'First Contact'}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {isArabic ? sla.timeToFirstContactAr : sla.timeToFirstContact}
                  </p>
                </div>
              </div>

              {/* Required Attempts */}
              <div className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isArabic ? 'المحاولات المطلوبة' : 'Required Attempts'}
                </span>
                <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {sla.requiredAttempts}
                </span>
              </div>

              {/* Attempt Cadence */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                <h5 className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 uppercase mb-2">
                  {isArabic ? 'جدول المحاولات' : 'Attempt Cadence'}
                </h5>
                <p className="text-sm text-indigo-600 dark:text-indigo-400 line-clamp-3">
                  {isArabic ? sla.attemptCadenceAr : sla.attemptCadence}
                </p>
              </div>

              {/* Notes if available */}
              {(sla.notes || sla.notesAr) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300 line-clamp-2">
                      {isArabic ? sla.notesAr : sla.notes}
                    </p>
                  </div>
                </div>
              )}

              {/* View Details Link */}
              <Link 
                to={`/sla-catalogue/channel/${sla.id}`}
                className="flex items-center justify-center gap-2 w-full py-3 mt-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors group"
              >
                {isArabic ? 'عرض التفاصيل' : 'View Details'}
                <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isArabic ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Stage SLAs Tab - Timeline View
// =============================================================================

const StageSLAsTab: React.FC<{ slas: StageSLA[]; isArabic: boolean }> = ({ 
  slas, 
  isArabic 
}) => {
  if (slas.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <Zap className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {isArabic ? 'لا توجد اتفاقيات مراحل' : 'No Stage SLAs'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {isArabic ? 'لا توجد اتفاقيات مراحل متاحة للعلامة التجارية المحددة' : 'No stage SLAs available for the selected brand'}
        </p>
      </div>
    );
  }

  const stageColors = [
    'from-blue-500 to-cyan-500',
    'from-indigo-500 to-violet-500',
    'from-purple-500 to-pink-500',
    'from-rose-500 to-orange-500',
    'from-amber-500 to-yellow-500',
    'from-lime-500 to-green-500',
    'from-emerald-500 to-teal-500',
    'from-cyan-500 to-blue-500',
    'from-blue-500 to-indigo-500',
    'from-violet-500 to-purple-500',
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-500" />
          {isArabic ? 'اتفاقيات مستوى الخدمة حسب المرحلة' : 'Stage SLAs'}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {slas.length} {isArabic ? 'مرحلة' : 'stages'}
        </span>
      </div>
      
      {/* Timeline View */}
      <div className="space-y-4">
        {slas.map((sla, index) => (
          <div 
            key={sla.id}
            className="group relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all"
          >
            {/* Left Color Bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${stageColors[index % stageColors.length]}`} />
            
            <div className="p-6 pl-8">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                {/* Stage Number & Name */}
                <div className="flex items-center gap-4 lg:w-1/3">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${stageColors[index % stageColors.length]} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {isArabic ? sla.stageNameAr : sla.stageName}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isArabic ? sla.ownerAr : sla.owner}
                    </p>
                  </div>
                </div>
                
                {/* Time Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                  <MetricPill 
                    icon={<Clock className="w-4 h-4" />}
                    label={isArabic ? 'الحد الأقصى' : 'Max'}
                    value={isArabic ? sla.maxDurationAr : sla.maxDuration}
                    variant="red"
                  />
                  <MetricPill 
                    icon={<Target className="w-4 h-4" />}
                    label={isArabic ? 'المستهدف' : 'Target'}
                    value={isArabic ? sla.targetDurationAr : sla.targetDuration}
                    variant="green"
                  />
                  <MetricPill 
                    icon={<Zap className="w-4 h-4" />}
                    label={isArabic ? 'أول إجراء' : 'First Action'}
                    value={isArabic ? sla.firstActionSLAAr : sla.firstActionSLA}
                    variant="blue"
                  />
                  <MetricPill 
                    icon={<RotateCcw className="w-4 h-4" />}
                    label={isArabic ? 'المتابعة' : 'Follow-up'}
                    value={isArabic ? sla.followUpSLAAr : sla.followUpSLA}
                    variant="purple"
                  />
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-3 lg:w-auto">
                  <Link 
                    to={`/pipeline/stage/${sla.stageId}`}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                    title={isArabic ? 'عرض المرحلة' : 'View Stage'}
                  >
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                  <Link 
                    to={`/sla-catalogue/stage/${sla.id}`}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                  >
                    {isArabic ? 'التفاصيل' : 'Details'}
                    <ChevronRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                  </Link>
                </div>
              </div>
              
              {/* Escalation Trigger */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-red-600 dark:text-red-400 uppercase">
                      {isArabic ? 'محفز التصعيد:' : 'Escalation Trigger:'}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {isArabic ? sla.escalationTriggerAr : sla.escalationTrigger}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Metric Pill Component
// =============================================================================

const MetricPill: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  variant: 'red' | 'green' | 'blue' | 'purple';
}> = ({ icon, label, value, variant }) => {
  const variants = {
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  };

  return (
    <div className={`rounded-xl p-3 ${variants[variant]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-sm font-bold">{value}</p>
    </div>
  );
};

// =============================================================================
// Escalations Tab
// =============================================================================

const EscalationsTab: React.FC<{ rules: EscalationRule[]; isArabic: boolean }> = ({ 
  rules, 
  isArabic 
}) => {
  const getTriggerConfig = (type: EscalationTriggerType) => {
    const configs = {
      warning: { 
        bg: 'bg-yellow-50 dark:bg-yellow-900/20', 
        border: 'border-yellow-200 dark:border-yellow-800',
        icon: <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />,
        label: isArabic ? 'تحذير' : 'Warning',
        labelBg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
      },
      breach: { 
        bg: 'bg-orange-50 dark:bg-orange-900/20', 
        border: 'border-orange-200 dark:border-orange-800',
        icon: <XCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />,
        label: isArabic ? 'خرق' : 'Breach',
        labelBg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
      },
      critical: { 
        bg: 'bg-red-50 dark:bg-red-900/20', 
        border: 'border-red-200 dark:border-red-800',
        icon: <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />,
        label: isArabic ? 'حرج' : 'Critical',
        labelBg: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      },
    };
    return configs[type];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          {isArabic ? 'قواعد التصعيد' : 'Escalation Rules'}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {rules.length} {isArabic ? 'قاعدة' : 'rules'}
        </span>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {rules.map((rule) => {
          const config = getTriggerConfig(rule.triggerType);
          return (
            <div 
              key={rule.id}
              className={`rounded-2xl border-2 p-6 ${config.bg} ${config.border} transition-all hover:shadow-lg`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                    {config.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {isArabic ? rule.nameAr : rule.name}
                    </h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${config.labelBg}`}>
                      {config.label}
                      {rule.thresholdPercent && ` @ ${rule.thresholdPercent}%`}
                    </span>
                  </div>
                </div>
                {rule.autoReassign && (
                  <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                    {isArabic ? 'إعادة تعيين تلقائي' : 'Auto-Reassign'}
                  </span>
                )}
              </div>
              
              {/* Trigger Description */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {isArabic ? 'المحفز:' : 'Trigger:'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isArabic ? rule.triggerAr : rule.trigger}
                </p>
              </div>
              
              {/* Escalate To */}
              <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-xl">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {isArabic ? 'يصعد إلى' : 'Escalate To'}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {isArabic ? rule.escalateToAr : rule.escalateTo}
                </p>
              </div>
              
              {/* Actions */}
              {rule.actions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                    {isArabic ? 'الإجراءات' : 'Actions'}
                  </p>
                  <ul className="space-y-1.5">
                    {(isArabic ? rule.actionsAr : rule.actions).slice(0, 3).map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Notification Channels */}
              {rule.notifyChannels.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {rule.notifyChannels.map((channel, idx) => (
                      <span 
                        key={idx}
                        className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-xs font-medium"
                      >
                        {channel}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// Exceptions Tab
// =============================================================================

const ExceptionsTab: React.FC<{ codes: SLAExceptionCode[]; isArabic: boolean }> = ({ 
  codes, 
  isArabic 
}) => {
  const getBehaviorConfig = (behavior: SLABehavior) => {
    const configs = {
      pause: { 
        icon: <Pause className="w-4 h-4" />,
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-700 dark:text-yellow-300',
        label: isArabic ? 'إيقاف مؤقت' : 'Pause'
      },
      reset: { 
        icon: <RotateCcw className="w-4 h-4" />,
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-700 dark:text-blue-300',
        label: isArabic ? 'إعادة تعيين' : 'Reset'
      },
      exempt: { 
        icon: <CheckCircle className="w-4 h-4" />,
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        label: isArabic ? 'معفى' : 'Exempt'
      },
    };
    return configs[behavior];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-500" />
          {isArabic ? 'رموز الاستثناء' : 'Exception Codes'}
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {codes.length} {isArabic ? 'رمز' : 'codes'}
        </span>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {codes.map((code) => {
          const config = getBehaviorConfig(code.slaBehavior);
          return (
            <div 
              key={code.code}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <span className="font-mono text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg font-bold text-gray-800 dark:text-gray-200">
                  {code.code}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${config.bg} ${config.text}`}>
                  {config.icon}
                  {config.label}
                </span>
              </div>
              
              {/* Label */}
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {isArabic ? code.labelAr : code.label}
              </h4>
              
              {/* Description */}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                {isArabic ? code.descriptionAr : code.description}
              </p>
              
              {/* Approval Required */}
              {code.requiresApproval && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-3">
                  <UserCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                      {isArabic ? 'يتطلب موافقة' : 'Requires Approval'}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {isArabic ? code.approverAr || code.approver : code.approver}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Valid Stages */}
              {code.validForStages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {code.validForStages.slice(0, 3).map((stage, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                    >
                      {stage}
                    </span>
                  ))}
                  {code.validForStages.length > 3 && (
                    <span className="px-2 py-0.5 text-gray-500 text-xs">
                      +{code.validForStages.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// Global Config Tab
// =============================================================================

const GlobalConfigTab: React.FC<{ config: ReturnType<typeof getGlobalConfig>; isArabic: boolean }> = ({ 
  config, 
  isArabic 
}) => {
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <Settings className="w-5 h-5 text-gray-500" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {isArabic ? 'الإعدادات العامة' : 'Global Configuration'}
        </h3>
      </div>
      
      {/* Business Hours Card */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
            <Calendar className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-xl font-bold">{isArabic ? 'ساعات العمل' : 'Business Hours'}</h4>
            <p className="text-white/80">{config.timezone}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-white/70 text-sm mb-1">{isArabic ? 'ساعات العمل' : 'Working Hours'}</p>
            <p className="text-2xl font-bold">{config.businessHours.start} - {config.businessHours.end}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm md:col-span-2">
            <p className="text-white/70 text-sm mb-2">{isArabic ? 'أيام العمل' : 'Working Days'}</p>
            <div className="flex flex-wrap gap-2">
              {(isArabic ? config.businessHours.daysAr : config.businessHours.days).map((day, idx) => (
                <span key={idx} className="px-3 py-1 bg-white/20 rounded-lg text-sm font-medium">
                  {day}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* After Hours Policy */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl border-2 border-amber-200 dark:border-amber-800 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
            <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
              {isArabic ? 'سياسة ما بعد ساعات العمل' : 'After-Hours Policy'}
            </h4>
            <p className="text-amber-800 dark:text-amber-200">
              {isArabic ? config.afterHoursPolicyAr : config.afterHoursPolicy}
            </p>
          </div>
        </div>
      </div>
      
      {/* SLA Clock Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pause Conditions */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Pause className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {isArabic ? 'شروط إيقاف ساعة الاتفاقية' : 'SLA Pause Conditions'}
            </h4>
          </div>
          <ul className="space-y-3">
            {(isArabic ? config.slaPauseConditionsAr : config.slaPauseConditions).map((condition, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 text-xs font-bold">
                  {idx + 1}
                </span>
                {condition}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Resume Conditions */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {isArabic ? 'شروط استئناف ساعة الاتفاقية' : 'SLA Resume Conditions'}
            </h4>
          </div>
          <ul className="space-y-3">
            {(isArabic ? config.slaResumeConditionsAr : config.slaResumeConditions).map((condition, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 text-xs font-bold">
                  {idx + 1}
                </span>
                {condition}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SLACatalogueOverview;
